import React from 'react';

import isEmpty from 'lodash/isEmpty';

import { LEARNING_EVENTS } from '@lumapps/learning-events/keys';
import { learningEventsRoute, learningSpeakersRoute } from '@lumapps/learning-events/routes';
import { isLearningEnabled } from '@lumapps/learning/ducks/selectors';
import {
    SideNavigationSection as Section,
    SideNavigationItem,
    type SideNavigationSectionProps,
} from '@lumapps/lumx-back-office/components/SideNavigation';
import { learningHatImage } from '@lumapps/lumx/custom-icons';
import { mdiSettings } from '@lumapps/lumx/icons';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { useLearningRoles } from '../../hooks/useLearningRoles';
import { LEARNING } from '../../keys';
import {
    learningCategoriesRoute,
    learningCertificatesRoute,
    learningLearnersRoute,
    learningAdministratorsRoute,
    learningTrainingCoursesRoute,
} from '../../routes';
import { MissionCenterPermitionRoles } from '../../types';

export const LearningAdminSideNavigation: React.FC<Partial<SideNavigationSectionProps>> = (props) => {
    const { translateKey } = useTranslate();
    const { roles, route } = useLearningRoles();
    const learningEnabled = useSelector(isLearningEnabled);

    if (isEmpty(roles) || (!learningEnabled && isEmpty(route))) {
        return null;
    }

    return (
        <Section {...props} header={translateKey(LEARNING.LEARNING)} id="learning">
            {(roles?.includes(MissionCenterPermitionRoles.CatalogManager) ||
                roles?.includes(MissionCenterPermitionRoles.TrainingManager) ||
                roles?.includes(MissionCenterPermitionRoles.EventsManager) ||
                roles?.includes(MissionCenterPermitionRoles.TrainingPublisher) ||
                roles?.includes(MissionCenterPermitionRoles.CertificateTemplateManager)) &&
                learningEnabled && (
                    <SideNavigationItem
                        label={translateKey(GLOBAL.CATALOG)}
                        icon={learningHatImage}
                        id="learning-catalog"
                    >
                        {roles.includes(MissionCenterPermitionRoles.CatalogManager) && (
                            <SideNavigationItem
                                label={translateKey(GLOBAL.CATEGORIES)}
                                route={learningCategoriesRoute}
                                id="learning-categories"
                            />
                        )}
                        {(roles?.includes(MissionCenterPermitionRoles.CatalogManager) ||
                            roles?.includes(MissionCenterPermitionRoles.TrainingManager) ||
                            roles?.includes(MissionCenterPermitionRoles.TrainingPublisher)) && (
                            <SideNavigationItem
                                label={translateKey(GLOBAL.TRAINING_COURSES)}
                                route={learningTrainingCoursesRoute}
                                id="learning-training-courses"
                            />
                        )}
                        {roles.includes(MissionCenterPermitionRoles.EventsManager) && (
                            <SideNavigationItem
                                label={translateKey(LEARNING_EVENTS.TITLE)}
                                route={learningEventsRoute}
                                id="learning-events"
                            />
                        )}
                        {(roles.includes(MissionCenterPermitionRoles.SuperAdmin) ||
                            roles.includes(MissionCenterPermitionRoles.CertificateTemplateManager)) && (
                            <SideNavigationItem
                                label={translateKey(LEARNING.LEARNING_CERTIFICATES)}
                                route={learningCertificatesRoute}
                                id="learning-certificates"
                            />
                        )}
                    </SideNavigationItem>
                )}
            {(roles?.includes(MissionCenterPermitionRoles.LearnerManager) ||
                roles?.includes(MissionCenterPermitionRoles.AdminManager) ||
                roles?.includes(MissionCenterPermitionRoles.EventsManager) ||
                roles?.includes(MissionCenterPermitionRoles.SuperAdmin)) &&
                learningEnabled && (
                    <SideNavigationItem
                        label={translateKey(GLOBAL.ROLES_SETTINGS)}
                        icon={mdiSettings}
                        id="learning-administration"
                    >
                        {roles.includes(MissionCenterPermitionRoles.LearnerManager) && (
                            <SideNavigationItem
                                label={translateKey(LEARNING.LEARNING_LEARNERS)}
                                route={learningLearnersRoute}
                                id="learning-learners"
                            />
                        )}
                        {roles?.includes(MissionCenterPermitionRoles.EventsManager) && (
                            <SideNavigationItem
                                label={translateKey(GLOBAL.SPEAKERS)}
                                route={learningSpeakersRoute}
                                id="learning-speakers"
                            />
                        )}
                        {roles.includes(MissionCenterPermitionRoles.AdminManager) && (
                            <SideNavigationItem
                                label={translateKey(GLOBAL.ADMINISTRATORS)}
                                route={learningAdministratorsRoute}
                                id="learning-managers"
                            />
                        )}
                    </SideNavigationItem>
                )}
        </Section>
    );
};

LearningAdminSideNavigation.displayName = 'SideNavigationItem';
